import {
  Box,
  Button,
  Chip,
  Divider,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import CommonHandle from "../../../../common/handles/common.handles";
import {
  DATETIME_FORMAT3,
  DATETIME_FORMAT4,
  DATE_REQ_FORMAT,
  TIME_FORMAT
} from "../../../../config/constants";
import {
  EMeetingRoomStatus,
  meetingRoomStatusColor,
  meetingRoomStatusTitle,
  TagType
} from "../../../../models/common/models.enum";
import { Wrapper } from "./styles";

import {
  IMeetingRoom,
  IMeetingSchedule,
  IUpdateMeetingScheduleReq,
  IMeetingParticipant
} from "../../../../models/meeting";

import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import { useApi } from "../../../../hooks/useApi.hook";
import MeetingService from "../../../../services/api/meeting.service";
import { toggleMessage } from "../../../../components/Toast/Toast";
import CreateRoundedIcon from "@mui/icons-material/CreateRounded";
import { toggleConfirmModal } from "../../../../components/ConfirmModal/ConfirmModal";
import moment from "moment";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import { ControlForm, Modal } from "../../../../components";
import InputForm from "../../../../components/controls/InputForm";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { TagInput } from "../../../WorkflowManagement/components/TagInput/TagInput";
import { ITag } from "../../../../models/user";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

type Props = {
  item?: IMeetingSchedule;
  isAdminUpdate: boolean;
  isUserUpdate: boolean;
  isUpdate: boolean;
  isRegister: boolean;
  reFetch: Function;
};

type RowProps = {
  title: string;
  icon: React.ReactNode;
  content?: string;
  contentBox?: React.ReactNode;
};

const EventDetailPopover = ({
  item,
  reFetch,
  isAdminUpdate,
  isUserUpdate,
  isUpdate,
  isRegister
}: Props) => {
  const { t, i18n } = useTranslation();

  const now = moment().format(DATETIME_FORMAT3);
  const [isCancel, setIsCancel] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [openEdit, setOpenEdit] = useState(false);
  const updateSchedule = useApi<IMeetingSchedule>({});
  const deleteSchedule = useApi<IMeetingSchedule>({});
  const meetingRoom = useApi<IMeetingRoom[]>({ isFetch: true });

  const [actionLeft, setActionLeft] = useState<{
    text: string;
    color?:
      | "inherit"
      | "primary"
      | "secondary"
      | "success"
      | "error"
      | "info"
      | "warning";
    variant?: "text" | "contained" | "outlined";
    onClick: Function;
  } | null>(null);
  const [actionRight, setActionRight] = useState<{
    text: string;
    color?:
      | "inherit"
      | "primary"
      | "secondary"
      | "success"
      | "error"
      | "info"
      | "warning";
    variant?: "text" | "contained" | "outlined";
    onClick: Function;
  } | null>(null);

  const [locale, setLocale] = useState<string>();

  useEffect(() => {
    setLocale(i18n.language);
    dayjs.locale(locale);
  }, [i18n.language]);

  const RowLayout = (props: RowProps) => {
    return (
      <Box display="flex" margin="4px 0">
        {props.icon}
        <Box display="flex" flexDirection="column" marginLeft="4px">
          <Box display="flex" marginBottom="4px">
            <Typography variant="caption" sx={{ color: "GrayText" }}>
              {props.title}
            </Typography>
          </Box>
          {props.contentBox ? (
            props.contentBox
          ) : (
            <Typography variant="subtitle2" sx={{ fontSize: "12px" }}>
              {props.content}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };
  const adminUpdateStatus = useApi<IMeetingSchedule>({});
  const userUpdateStatus = useApi<IMeetingSchedule>({});

  function handleAdminUpdateStatus(status: EMeetingRoomStatus) {
    adminUpdateStatus.request(
      MeetingService.adminUpdateStatus({
        id: item?.id ?? "",
        status: status
      }).then(res => {
        if (res.success) {
          toggleMessage({
            type: "success",
            message:
              res?.message ?? t("Meeting.EventDetail.Messages.UpdateSuccess")
          });
          reFetch();
        } else if (!res.success) {
          toggleMessage({
            type: "error",
            message:
              res?.message ?? t("Meeting.EventDetail.Messages.GenericError")
          });
        }
      })
    );
  }

  function handleUserUpdateStatus(status: EMeetingRoomStatus) {
    userUpdateStatus.request(
      MeetingService.userUpdateStatus({
        id: item?.id ?? "",
        status: status,
        ReasonForFancellation: inputValue
      }).then(res => {
        if (res.success) {
          toggleMessage({
            type: "success",
            message:
              res?.message ?? t("Meeting.EventDetail.Messages.UpdateSuccess")
          });
          reFetch();
        } else if (!res.success) {
          toggleMessage({
            type: "error",
            message:
              res?.message ?? t("Meeting.EventDetail.Messages.GenericError")
          });
        }
      })
    );
  }

  useEffect(() => {
    switch (item?.status) {
      case EMeetingRoomStatus.Pending:
        setActionRight(
          isAdminUpdate
            ? {
                text: t("Meeting.EventDetail.Actions.Approve"),
                variant: "contained",
                onClick: () => {
                  toggleConfirmModal({
                    title: t("Meeting.EventDetail.Confirm.ApproveTitle"),
                    content: t("Meeting.EventDetail.Confirm.ApproveContent"),
                    onSubmit: async () =>
                      handleAdminUpdateStatus(EMeetingRoomStatus.Confirmed),
                    submitText: t("Meeting.EventDetail.Confirm.SubmitText"),
                    type: "confirm"
                  });
                }
              }
            : null
        );
        setActionLeft(
          isAdminUpdate
            ? {
                text: t("Meeting.EventDetail.Actions.Reject"),
                color: "error",
                variant: "outlined",
                onClick: () => {
                  toggleConfirmModal({
                    title: t("Meeting.EventDetail.Confirm.RejectTitle"),
                    content: (
                      <Box>
                        <p>
                          {t("Meeting.EventDetail.Confirm.RejectContentLine1")}
                        </p>
                        <p>
                          {t("Meeting.EventDetail.Confirm.RejectContentLine2")}
                        </p>
                      </Box>
                    ),
                    onSubmit: async () => {
                      deleteSchedule.request(
                        MeetingService.deleteMeeting(item.id).then(response => {
                          if (response.success) {
                            toggleMessage({
                              type: "success",
                              message:
                                response?.message ??
                                t("Meeting.EventDetail.Messages.DeleteSuccess")
                            });
                            reFetch();
                          } else {
                            toggleMessage({
                              type: "error",
                              message:
                                response?.message ??
                                t("Meeting.EventDetail.Messages.GenericError")
                            });
                          }
                        })
                      );
                    },
                    submitText: t("Meeting.EventDetail.Confirm.SubmitText"),
                    type: "warning-red"
                  });
                }
              }
            : null
        );
        break;

      case EMeetingRoomStatus.Confirmed:
        const actionComplete: any =
          isAdminUpdate || isUserUpdate
            ? {
                text: t("Meeting.EventDetail.Actions.Complete"),
                variant: "contained",
                onClick: () => {
                  toggleConfirmModal({
                    title: t("Meeting.EventDetail.Confirm.CompleteTitle"),
                    content: t("Meeting.EventDetail.Confirm.CompleteContent"),
                    onSubmit: async () => {
                      if (isAdminUpdate) {
                        handleAdminUpdateStatus(EMeetingRoomStatus.Completed);
                      } else if (isUserUpdate) {
                        handleUserUpdateStatus(EMeetingRoomStatus.Completed);
                      }
                    },
                    submitText: t("Meeting.EventDetail.Confirm.SubmitText"),
                    type: "warning-red"
                  });
                }
              }
            : null;
        const actionCancel: any = isUserUpdate
          ? {
              text: t("Meeting.EventDetail.Actions.CancelRegister"),
              color: "warning",
              variant: "outlined",
              onClick: () => {
                setIsCancel(true);
                setActionLeft({
                  text: t("Meeting.EventDetail.Actions.Cancel"),
                  variant: "outlined",
                  color: "error",
                  onClick: () => {
                    setIsCancel(false);
                    setActionRight(actionComplete);
                    setActionLeft(actionCancel);
                  }
                });
                setActionRight({
                  text: t("Meeting.EventDetail.Actions.ConfirmCancel"),
                  variant: "contained",
                  onClick: () => {
                    toggleConfirmModal({
                      title: t(
                        "Meeting.EventDetail.Confirm.RequestCancelTitle"
                      ),
                      content: t(
                        "Meeting.EventDetail.Confirm.RequestCancelContent"
                      ),
                      onSubmit: async () => {
                        if (isAdminUpdate) {
                          handleAdminUpdateStatus(
                            EMeetingRoomStatus.WaitCancel
                          );
                        } else if (isUserUpdate) {
                          handleUserUpdateStatus(EMeetingRoomStatus.WaitCancel);
                        }
                      },
                      submitText: t("Meeting.EventDetail.Confirm.SubmitText"),
                      type: "warning"
                    });
                  }
                });
              }
            }
          : null;
        setActionRight(actionComplete);
        setActionLeft(actionCancel);
        break;

      case EMeetingRoomStatus.WaitCancel:
        setActionRight(
          isAdminUpdate
            ? {
                text: t("Meeting.EventDetail.Actions.Confirm"),
                variant: "contained",
                onClick: () => {
                  toggleConfirmModal({
                    title: t("Meeting.EventDetail.Confirm.ApproveCancelTitle"),
                    content: t(
                      "Meeting.EventDetail.Confirm.ApproveCancelContent"
                    ),
                    onSubmit: async () =>
                      handleAdminUpdateStatus(EMeetingRoomStatus.Cancel),
                    submitText: t("Meeting.EventDetail.Confirm.SubmitText"),
                    type: "warning"
                  });
                }
              }
            : null
        );
        break;

      default:
        break;
    }
  }, []);

  const validationSchema = Yup.object().shape({
    title: Yup.string().required(
      t("Meeting.EventDetail.Validation.TitleRequired")
    ),
    description: Yup.string().required(
      t("Meeting.EventDetail.Validation.DescriptionRequired")
    ),
    startDate: Yup.string().required(
      t("Meeting.EventDetail.Validation.StartDateRequired")
    ),
    roomID: Yup.string()
      .notOneOf([""], "You must select an option!")
      .required(),
    meetingParticipants: Yup.array()
      .required(t("Meeting.EventDetail.Validation.ParticipantsRequired"))
      .min(1, t("Meeting.EventDetail.Validation.ParticipantsRequired"))
  }) as any;

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors }
  } = useForm<any>({
    defaultValues: Object.assign(item!, {
      startDate: moment(item?.startDate).format(DATETIME_FORMAT3)
    }),
    resolver: yupResolver(validationSchema)
  });

  useEffect(() => {
    setValue(
      "meetingParticipants",
      item?.meetingParticipants.map(
        v =>
          ({
            id: v.meetingParticipantId,
            userName: v.userNameParticipant,
            fullName: v.userNameParticipant,
            type: TagType.User
          }) as ITag
      )
    );
  }, []);

  const onSubmit = (request: any) => {
    const req: IUpdateMeetingScheduleReq = {
      Id: request.id,
      RoomId: request.roomID,
      StartDate: moment(request.startDate).format(DATE_REQ_FORMAT),
      EndDate: request.endDate
        ? moment(request.endDate).format(DATE_REQ_FORMAT)
        : null,
      Title: request.title,
      Description: request.description,
      OtherMeetingParticipants: request.otherMeetingParticipants,
      MeetingParticipants: request.meetingParticipants.map(
        (participant: ITag) =>
          ({
            meetingParticipantId: participant.id,
            userNameParticipant: participant.userName,
            type: participant.type
          }) as IMeetingParticipant
      )
    };
    updateSchedule.request(
      MeetingService.update(req).then(res => {
        if (res.success) {
          toggleMessage({
            type: "success",
            message:
              res?.message ?? t("Meeting.EventDetail.Messages.UpdateSuccess")
          });
          reFetch();
          setOpenEdit(false);
        } else {
          toggleMessage({
            type: "error",
            message: res?.error ?? t("Meeting.EventDetail.Messages.UpdateError")
          });
        }
      })
    );
  };

  return (
    <Wrapper>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        padding="6px 14px"
      >
        <Box>
          <Typography
            variant="subtitle2"
            color={meetingRoomStatusColor(item?.status).color}
          >
            {item?.title}
          </Typography>
          <p style={{ fontSize: "12px", color: "GrayText" }}>
            {item?.description}
          </p>
        </Box>

        {isUpdate && (
          <Box display={!isAdminUpdate && !isRegister ? "none" : "flex"}>
            <Tooltip title={t("Meeting.EventDetail.TooltipDeleteSchedule")}>
              <Button
                size="small"
                onClick={() => {
                  toggleConfirmModal({
                    title: t("Meeting.EventDetail.Confirm.DeleteTitle"),
                    content: t("Meeting.EventDetail.Confirm.DeleteContent"),
                    onSubmit: async () => {
                      if (item?.id) {
                        deleteSchedule.request(
                          MeetingService.deleteMeeting(item.id).then(res => {
                            if (res.success) {
                              toggleMessage({
                                type: "success",
                                message:
                                  res?.message ??
                                  t(
                                    "Meeting.EventDetail.Messages.DeleteSuccess"
                                  )
                              });
                              reFetch();
                            } else {
                              toggleMessage({
                                type: "error",
                                message:
                                  res?.error ??
                                  t("Meeting.EventDetail.Messages.GenericError")
                              });
                              reFetch();
                            }
                          })
                        );
                      }
                    },
                    submitText: t("Meeting.EventDetail.Confirm.SubmitText"),
                    type: "warning-red"
                  });
                }}
              >
                <DeleteRoundedIcon />
              </Button>
            </Tooltip>
            <Tooltip title={t("Meeting.EventDetail.TooltipEditSchedule")}>
              <Button
                size="small"
                sx={{ marginLeft: "16px" }}
                onClick={() => {
                  meetingRoom.request(
                    MeetingService.getRooms({
                      CurrentPage: 1,
                      PageSize: 10
                    })
                  );
                  setOpenEdit(true);
                }}
              >
                <CreateRoundedIcon />
              </Button>
            </Tooltip>
            <Modal
              title={t("Meeting.EventDetail.ModalEditTitle")}
              textSubmit={t("Meeting.EventDetail.ModalSubmit")}
              textClose={t("Meeting.EventDetail.ModalClose")}
              open={openEdit}
              onSubmit={handleSubmit(onSubmit)}
              onClose={() => {
                setOpenEdit(false);
              }}
              loadingState={updateSchedule.loading}
            >
              <form
                onSubmit={handleSubmit(onSubmit)}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px"
                }}
              >
                <ControlForm
                  title={t("Meeting.EventDetail.Form.Title")}
                  isRequired
                  classname="brand-form"
                >
                  <InputForm
                    placeholder={t("Meeting.EventDetail.Form.TitlePlaceholder")}
                    required
                    name="title"
                    errors={errors}
                    control={control}
                    size="small"
                    disabled={updateSchedule.loading}
                  />
                </ControlForm>
                <ControlForm
                  title={t("Meeting.EventDetail.Form.Description")}
                  isRequired
                  classname="brand-form"
                >
                  <InputForm
                    placeholder={t(
                      "Meeting.EventDetail.Form.DescriptionPlaceholder"
                    )}
                    name="description"
                    required
                    errors={errors}
                    control={control}
                    size="small"
                    disabled={updateSchedule.loading}
                  />
                </ControlForm>

                <ControlForm
                  title={t("Meeting.EventDetail.Form.Room")}
                  isRequired
                  classname="brand-form"
                >
                  <Controller
                    name="roomID"
                    control={control}
                    defaultValue=""
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        disabled={updateSchedule.loading}
                        fullWidth
                        size="small"
                        defaultValue={""}
                        placeholder={t(
                          "Meeting.EventDetail.Form.RoomPlaceholder"
                        )}
                        sx={{ fontSize: "small" }}
                        error={!!errors.roomID}
                      >
                        {meetingRoom.data &&
                          meetingRoom.data?.map(room => (
                            <MenuItem
                              key={room.id}
                              value={room.id}
                              sx={{ fontSize: "small" }}
                            >
                              {room.roomName}
                            </MenuItem>
                          ))}
                      </Select>
                    )}
                  />
                  {errors.RoomId && (
                    <Typography
                      variant="caption"
                      marginLeft="16px"
                      color="error"
                    >
                      {t("Meeting.EventDetail.Form.RoomRequiredMessage")}
                    </Typography>
                  )}
                </ControlForm>

                <ControlForm
                  title={t("Meeting.EventDetail.Form.StartTime")}
                  classname="brand-form"
                >
                  <LocalizationProvider
                    dateAdapter={AdapterMoment}
                    adapterLocale={locale}
                  >
                    <DateTimePicker
                      name="startDate"
                      ampm={false}
                      format={DATETIME_FORMAT4}
                      defaultValue={moment(getValues("startDate"))}
                      sx={{ width: "100%" }}
                      onChange={e => {
                        const date = e ? e.format(DATETIME_FORMAT3) : now;
                        setValue("startDate", date);
                      }}
                      dayOfWeekFormatter={day => `${day.format("ddd")}`}
                    />
                  </LocalizationProvider>
                </ControlForm>

                <ControlForm
                  title={t("Meeting.EventDetail.Form.EndTime")}
                  classname="brand-form"
                >
                  <LocalizationProvider
                    dateAdapter={AdapterMoment}
                    adapterLocale={locale}
                  >
                    <DateTimePicker
                      name="endDate"
                      ampm={false}
                      format={DATETIME_FORMAT4}
                      defaultValue={moment(getValues("endDate"))}
                      sx={{ width: "100%" }}
                      onChange={e => {
                        const date = e ? e.format(DATETIME_FORMAT3) : null;
                        setValue("endDate", date);
                      }}
                      dayOfWeekFormatter={day => `${day.format("ddd")}`}
                    />
                  </LocalizationProvider>
                </ControlForm>

                <ControlForm
                  title={t("Meeting.EventDetail.Form.Participants")}
                  classname="brand-form"
                >
                  <TagInput
                    name={"meetingParticipants"}
                    control={control}
                    errors={errors}
                    isError={!!errors.meetingParticipants}
                    type={TagType.User}
                    placeholder={t(
                      "Meeting.EventDetail.Form.ParticipantsPlaceholder"
                    )}
                  />
                </ControlForm>

                <ControlForm
                  title={t("Meeting.EventDetail.Form.OtherParticipants")}
                  classname="brand-form"
                  infor={t("Meeting.EventDetail.Form.OtherParticipantsInfo")}
                >
                  <InputForm
                    placeholder={t(
                      "Meeting.EventDetail.Form.OtherParticipantsPlaceholder"
                    )}
                    name="otherMeetingParticipants"
                    errors={errors}
                    control={control}
                    size="small"
                    disabled={updateSchedule.loading}
                  />
                </ControlForm>
              </form>
            </Modal>
          </Box>
        )}
      </Box>
      <Divider />
      <Box padding="4px 12px" display="flex">
        <Box>
          <RowLayout
            title={t("Meeting.EventDetail.FieldStatus")}
            icon={<BookmarkBorderOutlinedIcon />}
            contentBox={
              <Chip
                label={t(meetingRoomStatusTitle(item?.status))}
                size="small"
                sx={{
                  backgroundColor: meetingRoomStatusColor(item?.status).bgcolor,
                  color: meetingRoomStatusColor(item?.status).color,
                  fontSize: "10px",
                  fontWeight: "600",
                  height: "20px"
                }}
              />
            }
          />
          <RowLayout
            title={t("Meeting.EventDetail.FieldDateTime")}
            icon={<EventOutlinedIcon />}
            content={`${CommonHandle.formatDate(
              item?.startDate
            )}\t${moment(item?.startDate).format(TIME_FORMAT)}${
              item?.endDate
                ? ` - ${moment(item.endDate).format(TIME_FORMAT)}`
                : ""
            }`}
          />
          <RowLayout
            title={t("Meeting.EventDetail.FieldLocation")}
            icon={<MeetingRoomOutlinedIcon />}
            content={item?.meetingRoom?.roomName ?? "..."}
          />
        </Box>
        <Divider orientation="vertical" flexItem sx={{ margin: "0 14px" }} />
        <Box>
          <RowLayout
            title={t("Meeting.EventDetail.FieldHost")}
            icon={<ManageAccountsOutlinedIcon />}
            content={item?.meetingRegistrant?.fullName ?? ""}
          />
          <RowLayout
            title={t("Meeting.EventDetail.FieldParticipants")}
            icon={<PeopleAltOutlinedIcon />}
            content={item?.meetingParticipants
              .map(user => user.userNameParticipant)
              .join(", ")}
          />
          <RowLayout
            title={t("Meeting.EventDetail.FieldOtherParticipants")}
            icon={<AccountCircleOutlinedIcon />}
            content={item?.otherMeetingParticipants}
          />
        </Box>
      </Box>
      <Divider />
      {isCancel && (
        <TextField
          placeholder={t("Meeting.EventDetail.PlaceholderCancelReason")}
          sx={{ margin: "12px", width: "calc(100% - 24px)" }}
          value={inputValue}
          size="small"
          onChange={e => {
            setInputValue(e.target.value);
          }}
        />
      )}
      {(isAdminUpdate || (!isAdminUpdate && isRegister)) && (
        <Box display="flex" marginBottom="8px" justifyContent="end">
          {actionLeft && (
            <Button
              variant={actionLeft?.variant}
              color={actionLeft?.color}
              size="small"
              className="event-action-button"
              onClick={() => actionLeft.onClick()}
            >
              {actionLeft?.text}
            </Button>
          )}
          {actionRight && (
            <Button
              variant={actionRight?.variant}
              color={actionRight?.color}
              size="small"
              className="event-action-button"
              onClick={() => actionRight.onClick()}
            >
              {actionRight?.text}
            </Button>
          )}
        </Box>
      )}
    </Wrapper>
  );
};

export default EventDetailPopover;
